import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { BookForm } from '@/components/BookForm/BookForm';
import type { BookFormInitialValue } from '@/components/BookForm/BookForm';
import { getBook, createBook, updateBook } from '@/api/books';
import type { BookCreateInput } from '@/api/types';
import styles from './BookFormPage.module.css';

export function BookFormPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const [initial, setInitial] = useState<BookFormInitialValue | null | 'loading'>('loading');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

  // Предупреждение при перезагрузке/закрытии вкладки
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Для большинства браузеров достаточно установки returnValue
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  const leaveWithoutSave = () => {
    setConfirmLeaveOpen(false);
    setIsDirty(false);
    navigate(-1);
  };

  const handleBack = () => {
    if (isDirty) {
      setConfirmLeaveOpen(true);
    } else {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setConfirmLeaveOpen(true);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (isNew) {
      setInitial({ title: '', placement: 'collection' });
      return;
    }
    getBook(id!)
      .then((book) => {
        if (!book) {
          setInitial(null);
          return;
        }
        setInitial({
          author: book.author,
          title: book.title,
          placement: book.placement,
          series: book.series,
          shelf: book.shelf,
          readStatus: book.readStatus,
          part: book.part,
          rating: book.rating,
          comment: book.comment,
          isbn: book.isbn,
          coverUrl: book.coverUrl,
        });
      })
      .catch(() => {
        setInitial(null);
      });
  }, [id, isNew]);

  const handleSubmit = (data: BookCreateInput) => {
    setLoading(true);
    setError(null);
    if (isNew) {
      createBook(data)
        .then(() => {
          setIsDirty(false);
          navigate(-1);
        })
        .catch((err) => setError(err instanceof Error ? err.message : 'Ошибка сохранения'))
        .finally(() => setLoading(false));
    } else {
      updateBook(id!, data)
        .then(() => {
          setIsDirty(false);
          navigate(-1);
        })
        .catch((err) => setError(err instanceof Error ? err.message : 'Ошибка сохранения'))
        .finally(() => setLoading(false));
    }
  };

  if (initial === 'loading') {
    return (
      <Box className={styles.root}>
        <Typography color="text.secondary">Загрузка…</Typography>
      </Box>
    );
  }

  if (initial === null) {
    return (
      <Box className={styles.root}>
        <Typography color="error">Книга не найдена</Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.root}>
      <MuiLink component="button" type="button" className={styles.backLink} onClick={handleBack}>
        ← Назад
      </MuiLink>
      <Box className={styles.formWrap}>
        <BookForm
          initialValue={initial}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitLabel={isNew ? 'Добавить' : 'Сохранить'}
          loading={loading}
          error={error}
          onDirtyChange={setIsDirty}
        />
      </Box>
      <Dialog
        open={confirmLeaveOpen}
        onClose={() => setConfirmLeaveOpen(false)}
      >
        <DialogTitle>Есть несохранённые изменения</DialogTitle>
        <DialogContent>
          <DialogContentText>
            У вас есть несохранённые изменения. Выйти без сохранения?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeaveOpen(false)} color="inherit">
            Остаться
          </Button>
          <Button onClick={leaveWithoutSave} color="error" variant="contained" autoFocus>
            Выйти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
